import {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { VideoItem, theme as hhgTheme } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { VideoItemType, VideoType } from '@hhgtech/hhg-components/types'
import { ChevronLeft, ChevronRight } from '@hhgtech/icons/arrow'
import { Carousel, Embla } from '@mantine/carousel'
import { Box, clsx, Flex, rem, useMantineTheme } from '@mantine/core'
import Autoplay from 'embla-carousel-autoplay'

import { useIsMobile } from 'hooks/useIsMobile'

const delayTime = 7 * 1000

export type SuggestedVideosProps = {
  id?: string
  title?: string
  videos?: VideoItemType[]
  className?: string
  style?: CSSProperties
}
export const SuggestedVideos: FC<SuggestedVideosProps> = (props) => {
  const { id, title, videos, className, style } = props
  const theme = useMantineTheme()
  const isMobile = useIsMobile()
  const autoplay = useRef(
    Autoplay({ delay: delayTime, stopOnInteraction: false }),
  )
  const [embla, setEmbla] = useState<Embla | null>(null)
  const [isPrev, setIsPrev] = useState(false)
  const [isNext, setIsNext] = useState(false)

  const isShowNavigation = useMemo(() => {
    if (isMobile) {
      return videos && videos?.length >= 2
    }
    return videos && videos.length > 1
  }, [isMobile])

  const handleScroll = useCallback(() => {
    if (!embla) return
    setIsPrev(embla.canScrollPrev())
    setIsNext(embla.canScrollNext())
  }, [embla])

  useEffect(() => {
    embla?.on('scroll', handleScroll)
    handleScroll()
    return () => {
      embla?.off('scroll', handleScroll)
    }
  }, [embla])

  if (!videos?.length) {
    return null
  }

  return (
    <Box id={id} className={className} style={style}>
      <Box
        className={clsx(
          'bg-white',
          'px-4 pt-4 pb-0 md:py-[10px]',
          'md:rounded-t-lg md:border-0 md:border-solid md:border-b md:border-neutral100',
        )}
      >
        <Text
          as="h3"
          size="s2"
          color={hhgTheme.colors.gray800}
          weight="semiBold"
          className="tracking-[-0.4px]"
        >
          {title}
        </Text>
      </Box>
      <Box
        className={clsx(
          'relative',
          'bg-white rounded-b-lg py-4 md:pt-4 md:pb-3',
        )}
      >
        <Carousel
          dragFree
          align={videos?.length === 1 ? 'center' : 'start'}
          containScroll={videos?.length === 1 ? undefined : 'trimSnaps'}
          slideSize="auto"
          slideGap={rem(12)}
          withControls={false}
          withIndicators={false}
          plugins={[autoplay.current]}
          onMouseEnter={autoplay.current.stop}
          onMouseLeave={autoplay.current.reset}
          getEmblaApi={setEmbla}
          classNames={{
            viewport: 'px-4',
          }}
        >
          {videos?.map((video, index) => (
            <Carousel.Slide key={`${index}`}>
              <VideoItem
                data={video}
                dataEventCategory="Health Tool"
                dataEventAction="Due Date - Helpful Tips - Click Suggest Video"
                styles={{
                  root: {
                    width: video.type === VideoType.SHORTS ? 103 : 330,
                    [theme.fn.largerThan('sm')]: {
                      width: video.type === VideoType.SHORTS ? 160 : 515,
                    },
                  },
                  title: {
                    [theme.fn.largerThan('sm')]: {
                      fontSize: rem(14),
                      lineHeight: rem(22),
                    },
                  },
                }}
              />
            </Carousel.Slide>
          ))}
        </Carousel>
        {isShowNavigation && (
          <>
            <Flex
              w={32}
              h={32}
              align="center"
              justify="center"
              bg={hhgTheme.colors.white}
              sx={{
                borderRadius: '100%',
                cursor: 'pointer',
                transform: 'translate(-50%, -50%)',
              }}
              onClick={() => embla?.scrollPrev()}
              className={clsx(
                'absolute z-[1] top-1/2 left-0 opacity-0 transition-all ease-linear duration-200 pointer-events-none',
                { 'opacity-100 pointer-events-auto': isPrev },
              )}
            >
              <ChevronLeft size={20} color={hhgTheme.colors.gray600} />
            </Flex>
            <Flex
              w={32}
              h={32}
              align="center"
              justify="center"
              bg={hhgTheme.colors.white}
              sx={{
                borderRadius: '100%',
                cursor: 'pointer',
                transform: 'translate(50%, -50%)',
              }}
              onClick={() => embla?.scrollNext()}
              className={clsx(
                'absolute z-[1] top-1/2 right-0 opacity-0 transition-all ease-linear duration-200 pointer-events-none',
                { 'opacity-100 pointer-events-auto': isNext },
              )}
            >
              <ChevronRight size={20} color={hhgTheme.colors.gray600} />
            </Flex>
          </>
        )}
      </Box>
    </Box>
  )
}
